import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'reactstrap';

import useBlockAction, { getBlockErrorMessage } from '../../../../hooks/useBlockAction';
import { IP_BLOCKLIST } from '../../../../constants/general';
import { BadgeListItem, Description } from '../../../../styledComponents/styledComponents';
import {
   BlockStatusDot,
   BlockStatusText,
   BlockTagInput,
   BlockActionButton,
   BlockStatusRow,
   BlockRowGrid,
   BlockRowControls,
   BlockRowStatusLine,
   BlockTagRow,
   BlockErrorText,
} from '../StyledComponents';

// isBlocked: true = blocked, false = not blocked, undefined = status still loading
function IpBlockRow({ ip, isBlocked, onActionSuccess }) {
   const [tag, setTag] = useState('');
   const [showTagInput, setShowTagInput] = useState(false);
   const [copied, setCopied] = useState(false);

   const { block, unblock, isLoading, isSuccess, error, reset } = useBlockAction({
      valueToBeBlocked: ip,
      type: IP_BLOCKLIST,
   });

   useEffect(() => {
      if (isSuccess) {
         onActionSuccess();
         reset();
         setTag('');
         setShowTagInput(false);
      }
   }, [isSuccess, onActionSuccess, reset]);

   const handleCopy = async () => {
      await navigator.clipboard.writeText(ip).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
   };

   const handleBlockClick = () => {
      setShowTagInput(true);
      reset();
   };

   const handleAbort = () => {
      setShowTagInput(false);
      setTag('');
      reset();
   };

   return (
      <BlockRowGrid>
         <BadgeListItem $fontSizePx={13} $cursor='pointer' onClick={handleCopy} className='vertical-padding'>
            {copied ? `✓ ${ip}` : ip}
         </BadgeListItem>

         <BlockRowControls>
            {isBlocked === undefined ? (
               ''
            ) : (
               <BlockRowStatusLine>
                  <BlockStatusRow>
                     <BlockStatusDot $blocked={isBlocked} />
                     <BlockStatusText $blocked={isBlocked}>{isBlocked ? 'Blocked' : 'Not blocked'}</BlockStatusText>
                  </BlockStatusRow>

                  {isBlocked ? (
                     <BlockActionButton $variant='unblock' disabled={isLoading} onClick={unblock}>
                        {isLoading ? 'Unblocking...' : 'Unblock'}
                     </BlockActionButton>
                  ) : (
                     <BlockActionButton
                        $variant={showTagInput ? 'unblock' : 'block'}
                        disabled={isLoading}
                        onClick={showTagInput ? handleAbort : handleBlockClick}
                     >
                        {showTagInput ? '✕ Cancel' : 'Configure IP Block'}
                     </BlockActionButton>
                  )}
               </BlockRowStatusLine>
            )}

            <Collapse isOpen={showTagInput && !isBlocked}>
               <Description marginBottomPx={3}>
                  <div>
                     <b>Note:</b> The block only affects new connections or the next reconnect and takes max. 6 minutes
                     to be effected.
                  </div>
                  <div>
                     Blocking for a running playout requires a reconnect to take effect. Blocked IPs will be
                     automatically unblocked after 24 hours.
                  </div>
               </Description>
               <BlockTagRow>
                  <BlockTagInput
                     style={{ width: 190 }}
                     value={tag}
                     onChange={(e) => setTag(e.target.value)}
                     placeholder='Comment (optional)'
                     disabled={isLoading}
                  />
                  <BlockActionButton $variant='block' disabled={isLoading} onClick={() => block(tag)}>
                     {isLoading ? 'Blocking...' : 'Confirm'}
                  </BlockActionButton>
               </BlockTagRow>
            </Collapse>

            {error && (
               <BlockErrorText>{getBlockErrorMessage(error, 'Action failed, please try again.')}</BlockErrorText>
            )}
         </BlockRowControls>
      </BlockRowGrid>
   );
}

IpBlockRow.propTypes = {
   ip: PropTypes.string.isRequired,
   isBlocked: PropTypes.bool,
   onActionSuccess: PropTypes.func.isRequired,
};

export default IpBlockRow;
